/**
 * ProductGrid Component
 * Responsive product grid with loading states
 */

import type { Product } from '~/lib/shopify/types';
import { ProductCard } from './ProductCard';

interface ProductGridProps {
  products: Product[];
  isLoading?: boolean;
  columns?: 2 | 3 | 4;
  listName?: string;
}

export function ProductGrid({ products, isLoading = false, columns = 4, listName }: ProductGridProps) {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  };

  if (isLoading) {
    return (
      <div className={`grid ${gridCols[columns]} gap-4 lg:gap-6`}>
        {Array.from({ length: 8 }).map((_, i) => (
          <ProductCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-20 h-20 mx-auto mb-6 bg-surface-coal rounded-2xl flex items-center justify-center">
          <svg className="w-10 h-10 text-accent-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-accent-white mb-2">No products found</h3>
        <p className="text-accent-silver">Try adjusting your filters or search terms.</p>
      </div>
    );
  }

  return (
    <div className={`grid ${gridCols[columns]} gap-4 lg:gap-6`}>
      {products.map((product, index) => (
        <ProductCard
          key={product.id}
          product={product}
          priority={index < 4}
          index={index}
          listName={listName}
        />
      ))}
    </div>
  );
}

function ProductCardSkeleton() {
  return (
    <div className="card overflow-hidden">
      <div className="aspect-square bg-surface-graphite skeleton" />
      <div className="p-4 space-y-3">
        <div className="h-4 bg-surface-slate skeleton rounded w-3/4" />
        <div className="h-4 bg-surface-slate skeleton rounded w-1/2" />
        <div className="flex gap-2">
          <div className="h-6 bg-surface-slate skeleton rounded w-16" />
          <div className="h-6 bg-surface-slate skeleton rounded w-16" />
        </div>
        <div className="h-6 bg-surface-slate skeleton rounded w-24" />
      </div>
    </div>
  );
}
